import { useState, cloneElement } from "react";
import { Tooltip, Box, Typography } from "@mui/material";
import type { TooltipProps } from "@mui/material";
import type { Term } from "@/lib/terms";

/**
 * Rich MUI Tooltip preset used for every financial-metric label in the
 * app. Accepts a `Term` object (title + body — see src/lib/terms.ts)
 * so every caller uses the same WHB-aligned copy.
 *
 * The passed child MUST be a DOM element (or use ref forwarding) so
 * MUI can attach pointer listeners. Wrap bare text in a <span> if
 * unsure.
 *
 * Click-to-toggle behavior: a tap/click on the child toggles the
 * tooltip open/closed. Desktop hover still works via onOpen/onClose.
 * MUI touch/focus listeners are disabled so a tap doesn't auto-open
 * and then get immediately toggled-off by the synthesized click.
 */
export function MetricTip({
  term,
  children,
  placement = "top",
  ...rest
}: {
  term: Term;
  children: React.ReactElement;
} & Omit<TooltipProps, "title" | "children">) {
  const [open, setOpen] = useState(false);

  const originalOnClick = (children.props as { onClick?: (e: React.MouseEvent) => void }).onClick;
  const wrapped = cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      setOpen((v) => !v);
      originalOnClick?.(e);
    }
  } as Partial<React.DOMAttributes<HTMLElement>>);

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableTouchListener
      disableFocusListener
      arrow
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "#1a1d25",
            color: "#e2e4e9",
            border: "1px solid #2d3040",
            borderRadius: 2,
            p: 1.25,
            maxWidth: 320,
            fontSize: "0.75rem"
          }
        },
        arrow: {
          sx: { color: "#1a1d25", "&::before": { border: "1px solid #2d3040" } }
        }
      }}
      title={
        <Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.6875rem",
              mb: 0.5
            }}
          >
            {term.title}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.45, color: "#e2e4e9" }}>
            {term.body}
          </Typography>
        </Box>
      }
      {...rest}
    >
      {wrapped}
    </Tooltip>
  );
}
